import React from 'react';
import { motion } from 'framer-motion';
import { User, Users, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useParallaxRef } from '@/utils/parallax';
import { Link } from 'react-router-dom';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { useGradientBackground } from '@/utils/gradientBackground';

interface PersonaCard {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const personas: PersonaCard[] = [
  {
    icon: <User className="w-6 h-6" />,
    title: 'Solo consultants & coaches',
    description:
      'You sell your expertise 1:1 or in small groups and want a brand and website that finally match the level of your work.',
  },
  {
    icon: <Users className="w-6 h-6" />,
    title: 'Micro-agencies & studios',
    description:
      'Small teams in branding, design, marketing, dev, or creative services that need a sharp, focused site to win better clients.',
  },
  {
    icon: <Building2 className="w-6 h-6" />,
    title: 'Specialist firms & practices',
    description:
      'Accounting, legal, HR, recruiting, or training firms that want their online presence to look as serious and premium as their domain.',
  },
];

export default function ServicesWhoSection() {
  // Parallax for subtitle (more pronounced)
  const subtitleRef = useParallaxRef({
    speed: -0.12,
    axis: 'y',
    maxTranslate: 10,
  });

  // Parallax for H2 (subtle, optional)
  const titleRef = useParallaxRef({
    speed: -0.06,
    axis: 'y',
    maxTranslate: 6,
  });

  // Reveal hook for cards
  const cardsReveal = useRevealOnce({ amount: 0.2 });

  // Card animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  const backgroundStyle = useGradientBackground();

  return (
    <section className="relative w-full py-16 md:py-24" style={backgroundStyle}>
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Heading Section */}
          <div className="text-center mb-12 md:mb-16">
            <h2
              ref={titleRef as any}
              className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6"
            >
              Built for people who sell expertise
            </h2>
            <p
              ref={subtitleRef as any}
              className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto"
            >
              Attain Domains is for people whose business depends on trust, not traffic. If you sell services or advice and your domain isn't pulling its weight yet, you're in the right place.
            </p>
          </div>

          {/* Persona Cards Grid */}
          <motion.div
            ref={cardsReveal.ref}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
            initial="hidden"
            animate={cardsReveal.controls}
            variants={{
              hidden: {},
              show: {
                transition: {
                  staggerChildren: 0.1,
                  delayChildren: 0.1,
                },
              },
            }}
          >
            {personas.map((persona, index) => (
              <motion.div key={index} variants={cardVariants}>
                <Card className="h-full border-slate-200 bg-white transition-all duration-200 ease-out hover:-translate-y-1 hover:shadow-lg">
                  <CardHeader className="text-center pb-4">
                    <div className="flex justify-center mb-4">
                      <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                        {persona.icon}
                      </div>
                    </div>
                    <CardTitle className="text-xl font-semibold">{persona.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-muted-foreground leading-relaxed">{persona.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Optional Subtle Link */}
          <div className="text-center mt-12 md:mt-16">
            <Link
              to="/contact"
              className="text-blue-600 hover:text-blue-700 hover:underline text-sm md:text-base transition-colors duration-200"
            >
              Not sure if this fits you? Book a free consult
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

